import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ChatMessage, Conversation } from "@/lib/chat";
import {
  fetchMessages,
  markConversationRead,
  sendMessage,
} from "@/lib/chat";
import QuickReplies from "@/pages/messages/components/QuickReplies";
import {
  buyerQuickReplies,
  fillQuickReply,
  sellerQuickReplies,
} from "@/pages/messages/quickReplies";

interface ConversationViewProps {
  conversation: Conversation | null;
  loading: boolean;
  conversationId?: string;
  currentUserId: string;
  onMessageSent: () => void;
}

function messageTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("es-DO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
  });
}

export default function ConversationView({
  conversation,
  loading,
  conversationId,
  currentUserId,
  onMessageSent,
}: ConversationViewProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(true);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!conversation) return;
    let cancelled = false;

    const load = async () => {
      try {
        const data = await fetchMessages(conversation.id);
        if (cancelled) return;
        setMessages(data);
        setMsgError(null);
        // Marcar como leído (idempotente).
        markConversationRead(conversation.id, currentUserId).catch(() => {});
      } catch (e) {
        if (!cancelled) {
          setMsgError(
            e instanceof Error ? e.message : "No se pudieron cargar los mensajes.",
          );
        }
      } finally {
        if (!cancelled) setMsgLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversation, currentUserId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!conversationId) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center rounded-lg border border-background-200/70 bg-background-50 px-4 text-center">
        <div>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100">
            <i className="ri-chat-3-line text-3xl text-foreground-300"></i>
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
            Tus mensajes
          </p>
          <p className="mt-1 max-w-xs text-sm text-foreground-500">
            Seleccioná una conversación de la izquierda para empezar a chatear.
          </p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center rounded-lg border border-background-200/70 bg-background-50 px-4 text-center">
        {loading ? (
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        ) : (
          <div>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-error-warning-line text-3xl text-accent-700"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              Conversación no disponible
            </p>
          </div>
        )}
      </div>
    );
  }

  async function handleSend() {
    const body = draft.trim();
    if (!body || !conversation || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const msg = await sendMessage(conversation.id, currentUserId, body);
      setMessages((prev) => [...prev, msg]);
      setDraft("");
      onMessageSent();
    } catch (e) {
      setSendError(
        e instanceof Error ? e.message : "No se pudo enviar el mensaje.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
      {/* Encabezado de la conversación */}
      <div className="flex shrink-0 items-center gap-3 border-b border-background-200/60 px-4 py-3">
        <Link
          to="/mensajes"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900 lg:hidden"
        >
          <i className="ri-arrow-left-line"></i>
        </Link>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
          {conversation.other_name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground-950">
            {conversation.other_name}
          </p>
          <Link
            to={`/producto/${conversation.product_id}`}
            className="block cursor-pointer truncate text-xs text-foreground-500 hover:text-primary-600"
          >
            {conversation.product_title}
          </Link>
        </div>
      </div>

      {/* Mensajes */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-background-50 px-4 py-4"
      >
        {msgLoading ? (
          <div className="flex h-full items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : msgError ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <p className="text-sm text-foreground-500">{msgError}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100">
              <i className="ri-mail-send-line text-xl text-foreground-400"></i>
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground-900">
              Empezá la conversación
            </p>
            <p className="mt-1 max-w-xs text-xs text-foreground-500">
              Enviale un mensaje a {conversation.other_name} sobre esta
              publicación.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => {
              const mine = m.sender_id === currentUserId;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                      mine
                        ? "rounded-br-sm bg-primary-500 text-white"
                        : "rounded-bl-sm bg-background-100 text-foreground-900"
                    }`}
                  >
                    <p className="whitespace-pre-line break-words">{m.body}</p>
                    <p
                      className={`mt-1 text-right text-[10px] ${
                        mine ? "text-white/70" : "text-foreground-400"
                      }`}
                    >
                      {messageTime(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Entrada */}
      <div className="shrink-0 border-t border-background-200/60 p-3">
        {sendError && (
          <p className="mb-2 text-xs font-medium text-accent-700">{sendError}</p>
        )}
        <QuickReplies
          replies={
            conversation.seller_id === currentUserId
              ? sellerQuickReplies
              : buyerQuickReplies
          }
          onPick={(text) => {
            setDraft(fillQuickReply(text, conversation.product_title));
            textareaRef.current?.focus();
          }}
        />
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            placeholder="Escribí un mensaje…"
            className="max-h-32 min-h-[42px] flex-1 resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            className="flex h-[42px] w-[42px] shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary-500 text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Enviar mensaje"
          >
            {sending ? (
              <i className="ri-loader-4-line animate-spin text-lg"></i>
            ) : (
              <i className="ri-send-plane-fill text-lg"></i>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}